import { useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { format as dateFormat } from "date-fns";
import moment from "moment";
import fileDownload from "js-file-download";
import axios from "axios";
import { OverlayTrigger } from "react-bootstrap";

import { useWatch } from "react-hook-form";
import { TiClipboard, TiDownload } from "react-icons/ti";
import { ImPrinter } from "react-icons/im";

// Components
import { TooltipStandard } from "../../../../Utility/TooltipStandard";
import ServiceTypeToggle from "../../../ServiceTypeToggle";
import LoadingModal from "../../../../LoadingModal";

// Hooks
import useDecodeToken from "../../../../../hooks/useDecodeToken";

// Constants
import {
  accesses,
  batchPrintMaxDateRange,
  clientsToBeExcludedFromPrintingLabels,
} from "../../../../../constants";
import { apiUrl } from "../../../../../config";
import { checkIfClient } from "../../constants";

// Services

import {
  // getDeliveryHistory,
  getExportOrderHistory,
  // getExportOrderHistory,
  // voidOrder as voidOrderEndpoint,
  // getLabelsForMultipleDeliveries,
  // printManifest,
  // simplifiedManifest,
  // getDeliveriesByBarcodes,
  // getDeliveriesByInvoices,
} from "../../../../../services/deliveryService";

import { PrintManifestModal, BatchPrintModal } from "../modals";
import handleApiError from "../../../../../helpers/handleApiError";

function OrderHistoryIcons({
  mode,
  onChangeMode,
  doesOrderExist = false,
  orders = [],
  isLoading = false,
}) {
  const history = useHistory();
  const [decodeToken] = useDecodeToken();
  const [isLoadingRequest, setIsLoadingRequest] = useState(false);
  const [showBatchPrintModal, setShowBatchPrintModal] = useState(false);

  const { control } = useFormContext();

  const [fromDate, toDate] = useWatch({
    control,
    name: ["fromDate", "toDate"],
  });

  const printManifestModalRef = useRef(null);

  const validDateRangeForBatchPrinting = () => {
    if (!fromDate || !toDate) return false;

    const from = moment(fromDate);
    const to = moment(toDate);
    const diffInDays = to.diff(from, "days");

    return diffInDays < batchPrintMaxDateRange;
  };

  const isClient = useMemo(() => checkIfClient(decodeToken), [decodeToken]);

  const toolTipStandarMessage = () => {
    const isValidDateRange = validDateRangeForBatchPrinting();

    if (isLoading) {
      return "Your orders are currently being fetched. Please wait for them to finish loading before batch printing.";
    }

    if (!isValidDateRange) {
      return "Batch printing only supports up to a 3 day date range (inclusive)";
    }

    if (!doesOrderExist) {
      return "No orders exist for selected date range";
    }

    return "We're not sure what's wrong";
  };

  const handleShowPrintManifestModal = () => {
    printManifestModalRef.current?.showPrintManifestModal();
  };

  const handleExportOnClick = () => {
    // check if fromDate and toDate are valid (not null)
    if (!fromDate || !toDate) return;
    // check if toDate is before fromDate (Ex: fromDate: 2023-01-10, toDate: 2023-01-05 => invalid)
    if (moment(toDate).isBefore(moment(fromDate))) return;

    const exportOrders = async () => {
      setIsLoadingRequest(true);

      try {
        const { data } = await getExportOrderHistory(fromDate, toDate);

        fileDownload(data, "OrderHistory.xlsx");
        setIsLoadingRequest(false);
      } catch (error) {
        setIsLoadingRequest(false);

        handleApiError(error, history);
      }
    };

    exportOrders();
  };

  const handleShowBatchPrintModal = () => {
    setShowBatchPrintModal(true);
    // show batch print modal
  };

  const handleCloseBatchPrintModal = () => {
    setShowBatchPrintModal(false);
  };

  const handleBatchPrint = () => {
    // validations before proceeding to batch print
    if (!fromDate || !toDate) return;

    if (moment(toDate).isBefore(moment(fromDate))) return;

    if (isLoading) return;

    const batchPrintConfirmed = async () => {
      setIsLoadingRequest(true);
      setShowBatchPrintModal(false);
      try {
        // const { data } = await getDeliveryHistory(fromDate, toDate, isClient);

        // const { deliveryList: foundOrders } = data;

        let result = orders?.map((item) => item.deliveryId);

        if (result?.length === 0) {
          toast.error("There are no orders to batch print.");
          setIsLoadingRequest(false);
          return;
        }

        const response = await axios.post(
          `${apiUrl}Delivery/GetLabelsForMultipleDeliveries/`,
          { deliveryId: result.toString(), isBatch: true, isClient: true },

          {
            responseType: "arraybuffer",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/pdf",
            },
          },
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          "Batch Print for " +
            fromDate +
            " to " +
            toDate +
            " printed on " +
            dateFormat(new Date(), "MM-dd-yyyy") +
            ".pdf",
        ); //or any other extension
        document.body.appendChild(link);
        link.click();

        toast.success("Batch printing has finished.");

        setIsLoadingRequest(false);
      } catch (error) {
        setIsLoadingRequest(false);
        toast.error("There was an error while batch printing.");
        handleApiError(error, history);
      }
    };

    batchPrintConfirmed();
  };

  return (
    <div className="tw-text-right tw-mb-3 md:tw-absolute md:tw-top-0 md:tw-right-0">
      <div className="tw-flex tw-gap-x-1">
        <LoadingModal active={isLoadingRequest} />

        {isClient && (
          <ServiceTypeToggle mode={mode} onChangeMode={onChangeMode} />
        )}

        {(decodeToken?.[accesses.IsMainAccount] ||
          decodeToken?.[accesses.Deltrans]) && (
          <TiClipboard
            role="button"
            className="no-copy order-history-form-icon"
            title="Print Manifest"
            size={25}
            onClick={handleShowPrintManifestModal}
          />
        )}

        <PrintManifestModal ref={printManifestModalRef} />

        {isClient && (
          <TiDownload
            role="button"
            className="no-copy order-history-form-icon"
            alt="Export Orders"
            title="Export Orders"
            size={25}
            style={{ marginTop: "-1px", marginRight: "4px" }}
            onClick={handleExportOnClick}
          />
        )}

        {isClient &&
          !clientsToBeExcludedFromPrintingLabels.includes(
            decodeToken?.ClientId,
          ) &&
          ((decodeToken?.[accesses.IsMainAccount] ||
            decodeToken?.[accesses.Deltrans]) &&
          validDateRangeForBatchPrinting() &&
          doesOrderExist &&
          !isLoading ? (
            <ImPrinter
              className="no-copy order-history-form-icon"
              role="button"
              data-toggle="tooltip"
              data-placement="top"
              title="Batch Print"
              onClick={handleShowBatchPrintModal}
              size={25}
            />
          ) : (
            <OverlayTrigger
              flip
              placement="top"
              overlay={
                <TooltipStandard>{toolTipStandarMessage()}</TooltipStandard>
              }
            >
              <ImPrinter
                className="no-copy order-history-form-icon disabled-gray"
                role="button"
                data-toggle="tooltip"
                data-placement="top"
                title="Batch Print"
                size={25}
              />
            </OverlayTrigger>
          ))}

        <BatchPrintModal
          show={showBatchPrintModal}
          onClose={handleCloseBatchPrintModal}
          onConfirmation={handleBatchPrint}
        />
      </div>
    </div>
  );
}

export default OrderHistoryIcons;
